import { App as AntdApp } from "antd"
import type React from "react"
import { useEffect, lazy, Suspense, useRef } from "react"
import { antdUtils } from "@/utils/antdUtil"
import { Navigate, type RouteObject } from "react-router"
import { useUserStore } from "@/stores/userStore"
import { AuthenticatedLayout } from "@/layouts/AuthenticatedLayout"
import { createBrowserRouter, RouterProvider } from "react-router-dom"
import routes from "@/router"

const LoginComponent = lazy(() => import("@/views/login"))
const NotFoundComponent = lazy(() => import("@/views/error/404"))
const ForbiddenComponent = lazy(() => import("@/views/error/403"))
const ServerErrorComponent = lazy(() => import("@/views/error/500"))

/**
 * Login Guard. Tránh user đã login vào trang login.
 */
function LoginGuard({ children }: { children: React.ReactNode }) {
  const { isLogin } = useUserStore.getState()

  if (isLogin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

/**
 * Root Path Redirect. Redirect trang chủ.
 */
function RootRedirect() {
  const { isLogin } = useUserStore.getState()

  return <Navigate to={isLogin ? "/dashboard" : "/login"} replace />
}

const App: React.FC = () => {
  const { notification, message, modal } = AntdApp.useApp()

  useEffect(() => {
    antdUtils.setMessageInstance(message)
    antdUtils.setNotificationInstance(notification)
    antdUtils.setModalInstance(modal)
  }, [])

  const routerRef = useRef<ReturnType<typeof createBrowserRouter> | null>(null)
  if (routerRef.current === null) {
    routerRef.current = createBrowserRouter(createBaseRoutes(routes))
  }

  return <RouterProvider router={routerRef.current} />
}

export function createBaseRoutes(dynamicRoutes: RouteObject[] = []): RouteObject[] {
  return [
    {
      path: "/",
      element: <RootRedirect />,
    },
    {
      path: "/login",
      element: (
        <LoginGuard>
          <Suspense fallback={null}>
            <LoginComponent />
          </Suspense>
        </LoginGuard>
      ),
    },
    {
      element: <AuthenticatedLayout />,
      children: [
        {
          path: "/404",
          element: (
            <Suspense fallback={null}>
              <NotFoundComponent />
            </Suspense>
          ),
        },
        {
          path: "/403",
          element: (
            <Suspense fallback={null}>
              <ForbiddenComponent />
            </Suspense>
          ),
        },
        {
          path: "/500",
          element: (
            <Suspense fallback={null}>
              <ServerErrorComponent />
            </Suspense>
          ),
        },
        ...dynamicRoutes,
        {
          path: "*",
          element: (
            <Suspense fallback={null}>
              <NotFoundComponent />
            </Suspense>
          ),
        },
      ],
    },
  ]
}

export default App
